import React, { useState, useMemo, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronRight, Calendar, Clock, AlertTriangle, Settings, User, FileText, ExternalLink } from 'lucide-react';
import type { Todo, TaskStatus } from '../types';

interface ListViewProps {
  todos: Todo[];
  onTodoClick: (todo: Todo) => void;
  onTodoStatusChange: (todoId: string, status: TaskStatus) => void;
  onAddChild: (parentId: string) => void;
  onMakeChild: (todoId: string, parentId: string) => void;
  onStartPomodoro?: (todo: Todo) => void;
  onToggleActiveWeek?: (todoId: string, isActive: boolean) => void;
  allTodos: Todo[];
  teamMembers?: { id: string; name: string; email: string; }[];
  isPersonalMode?: boolean;
}

type SortField = 'title' | 'status' | 'horizon' | 'due_date' | 'created_time' | 'completed_date' | 'last_modified_time' | 'creator_id' | 'assigned_to_id' | 'estimated_time_minutes';
type SortDirection = 'asc' | 'desc';

interface ColumnConfig {
  key: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  visible: boolean;
  sortable: boolean;
  width?: string;
}

// Enhanced column configuration with all available fields
const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'title', label: 'Task', visible: true, sortable: true, width: '280px' },
  { key: 'status', label: 'Status', visible: true, sortable: true },
  { key: 'horizon', label: 'Horizon', visible: true, sortable: true },
  { key: 'due_date', label: 'Due Date', icon: AlertTriangle, visible: true, sortable: true },
  { key: 'completed_date', label: 'Completed Date', icon: Calendar, visible: false, sortable: true },
  { key: 'created_time', label: 'Created', icon: Clock, visible: false, sortable: true },
  { key: 'last_modified_time', label: 'Last Modified', icon: Clock, visible: false, sortable: true },
  { key: 'notes', label: 'Notes', icon: FileText, visible: false, sortable: true },
  { key: 'source', label: 'Source', icon: ExternalLink, visible: false, sortable: true },
  { key: 'parent_id', label: 'Parent Task', icon: User, visible: false, sortable: false },
  { key: 'creator_id', label: 'Creator', icon: User, visible: false, sortable: false },
  { key: 'assigned_to_id', label: 'Assigned To', icon: User, visible: false, sortable: false },
  { key: 'estimated_time_minutes', label: 'Estimated Time', icon: Clock, visible: false, sortable: true },
];

// Storage key for user preferences (will be user-specific when auth is added)
const STORAGE_KEY = 'todoapp_listview_columns';

const ListView: React.FC<ListViewProps> = ({
  todos,
  onTodoClick,
  allTodos,
  teamMembers
}) => {
  const [sortField, setSortField] = useState<SortField>('created_time');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showInProgress, setShowInProgress] = useState(true);
  const [showCompleted, setShowCompleted] = useState(true);
  
  // Column visibility state with localStorage persistence
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const savedColumns = JSON.parse(saved);
        // Merge with default columns to handle new columns added in updates
        return DEFAULT_COLUMNS.map(defaultCol => {
          const savedCol = savedColumns.find((col: ColumnConfig) => col.key === defaultCol.key);
          return savedCol ? { ...defaultCol, visible: savedCol.visible } : defaultCol;
        });
      }
    } catch (error) {
      console.warn('Failed to load column preferences:', error);
    }
    return DEFAULT_COLUMNS;
  });

  // Save column preferences to localStorage
  const saveColumnPreferences = useCallback((newColumns: ColumnConfig[]) => {
    try {
      const columnsToSave = newColumns.map(col => ({ key: col.key, visible: col.visible }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(columnsToSave));
    } catch (error) {
      console.warn('Failed to save column preferences:', error);
    }
  }, []);

  // Toggle column visibility
  const toggleColumn = useCallback((columnKey: string) => {
    setColumns(prev => {
      const newColumns = prev.map(col => 
        col.key === columnKey ? { ...col, visible: !col.visible } : col
      );
      saveColumnPreferences(newColumns);
      return newColumns;
    });
  }, [saveColumnPreferences]);

  // Reset to default columns
  const resetColumns = useCallback(() => {
    setColumns(DEFAULT_COLUMNS);
    saveColumnPreferences(DEFAULT_COLUMNS);
  }, [saveColumnPreferences]);

  // Get visible columns
  const visibleColumns = useMemo(() => columns.filter(col => col.visible), [columns]);

  // Helper function to get assignee name
  const getAssigneeName = (assigneeId?: string) => {
    if (!assigneeId || !teamMembers?.length) return assigneeId || '-';
    const member = teamMembers.find(m => m.id === assigneeId);
    return member ? `${member.name} (${member.email})` : assigneeId;
  };

  // Helper function to get creator name
  const getCreatorName = (creatorId?: string) => {
    if (!creatorId || !teamMembers?.length) return creatorId || '-';
    const member = teamMembers.find(m => m.id === creatorId);
    return member ? `${member.name} (${member.email})` : creatorId;
  };

  // Organize todos by status
  const organizedTodos = useMemo(() => {
    const inProgressTodos = todos.filter(todo => 
      ['not_started', 'in_progress', 'waiting'].includes(todo.status)
    );
    const completedTodos = todos.filter(todo => todo.status === 'completed');
    
    return {
      inProgress: inProgressTodos,
      completed: completedTodos
    };
  }, [todos]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortTodos = useCallback((todosToSort: Todo[]) => {
    return [...todosToSort].sort((a, b) => {
      let aValue: unknown = a[sortField];
      let bValue: unknown = b[sortField];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert dates to comparable format
      if (sortField.includes('date') || sortField.includes('time')) {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Handle horizon sorting (now > next > soon > later > someday)
      if (sortField === 'horizon') {
        const horizonOrder: Record<string, number> = { now: 0, next: 1, soon: 2, later: 3, someday: 4 };
        aValue = aValue ? (horizonOrder[aValue as string] ?? 5) : 5;
        bValue = bValue ? (horizonOrder[bValue as string] ?? 5) : 5;
      }

      // Handle status sorting
      if (sortField === 'status') {
        const statusOrder: Record<string, number> = {
          not_started: 1,
          in_progress: 2,
          waiting: 3,
          completed: 4
        };
        aValue = statusOrder[aValue as TaskStatus] || 0;
        bValue = statusOrder[bValue as TaskStatus] || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDirection]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in_progress': return 'text-blue-600 bg-blue-50';
      case 'waiting': return 'text-yellow-600 bg-yellow-50';
      case 'not_started': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const SortButton: React.FC<{ field: SortField; children: React.ReactNode }> = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-left font-medium text-gray-700 hover:text-gray-900 transition-colors"
    >
      <span>{children}</span>
      {sortField === field && (
        sortDirection === 'asc' ? 
          <ChevronUp className="w-4 h-4" /> : 
          <ChevronDown className="w-4 h-4" />
      )}
    </button>
  );

  const renderCellContent = (todo: Todo, columnKey: string) => {
    switch (columnKey) {
      case 'title':
        return (
          <div className="flex items-center">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900">
                {todo.title}
              </div>
              {todo.description && (
                <div className="text-sm text-gray-500 line-clamp-2">
                  {todo.description}
                </div>
              )}
              {todo.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {todo.tags.slice(0, 3).map(tag => (
                    <span 
                      key={tag}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                    </span>
                  ))}
                  {todo.tags.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{todo.tags.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      case 'status':
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(todo.status)}`}>
            {todo.status.replace('_', ' ')}
          </span>
        );
      case 'horizon':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-600 bg-gray-50">
            {todo.horizon || 'Inbox'}
          </span>
        );
      case 'due_date':
        return formatDate(todo.due_date);
      case 'completed_date':
        return formatDate(todo.completed_date);
      case 'created_time':
        return formatDate(todo.created_time);
      case 'last_modified_time':
        return formatDate(todo.last_modified_time);
      case 'notes':
        return todo.notes ? (
          <div className="text-sm text-gray-600 truncate max-w-xs" title={todo.notes}>
            {todo.notes}
          </div>
        ) : '-';
      case 'source':
        return todo.source || '-';
      case 'parent_id':
        if (todo.parent_id) {
          const parent = allTodos.find(t => t.id === todo.parent_id);
          return parent ? (
            <div className="text-sm text-blue-600 truncate max-w-xs" title={parent.title}>
              {parent.title}
            </div>
          ) : 'Unknown';
        }
        return '-';
      case 'creator_id':
        return getCreatorName(todo.creator_id);
      case 'assigned_to_id':
        return getAssigneeName(todo.assigned_to_id);
      case 'estimated_time_minutes':
        return todo.estimated_time_minutes ? (
          <div className="text-sm text-gray-600">
            {todo.estimated_time_minutes} minutes
          </div>
        ) : '-';
      default:
        return '-';
    }
  };

  const renderTaskTable = (tasks: Todo[], title: string, isCollapsible: boolean = false, isExpanded: boolean = true, onToggle?: () => void) => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {isCollapsible && onToggle && (
            <button
              onClick={onToggle}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <span className="text-sm text-gray-500">
            ({tasks.length} task{tasks.length !== 1 ? 's' : ''})
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {tasks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No {title.toLowerCase()} found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {visibleColumns.map(column => (
                      <th key={column.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={column.width ? { width: column.width, minWidth: column.width, maxWidth: column.width } : undefined}>
                        {column.sortable ? (
                          <SortButton field={column.key as SortField}>
                            <div className="flex items-center space-x-1">
                              {column.icon && <column.icon className="w-4 h-4" />}
                              <span>{column.label}</span>
                            </div>
                          </SortButton>
                        ) : (
                          <div className="flex items-center space-x-1">
                            {column.icon && <column.icon className="w-4 h-4" />}
                            <span>{column.label}</span>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortTodos(tasks).map((todo) => (
                    <tr 
                      key={todo.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => onTodoClick(todo)}
                    >
                      {visibleColumns.map(column => (
                        <td
                          key={column.key}
                          className={`px-6 py-4 text-sm text-gray-900 ${column.key === 'title' ? 'whitespace-normal break-words' : 'whitespace-nowrap'}`}
                          style={column.width ? { width: column.width, minWidth: column.width, maxWidth: column.width } : undefined}
                        >
                          {renderCellContent(todo, column.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">All Tasks</h2>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {todos.length} task{todos.length !== 1 ? 's' : ''}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>Columns</span>
            </button>
            
            {showColumnSettings && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">Column Visibility</h3>
                    <button
                      onClick={resetColumns}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Reset
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {columns.map(column => (
                      <label key={column.key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={column.visible}
                          onChange={() => toggleColumn(column.key)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 flex items-center">
                          {column.icon && <column.icon className="w-3 h-3 mr-1" />}
                          {column.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      Column preferences are saved automatically
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {todos.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-gray-500 mb-4">No tasks found</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* In Progress Tasks */}
          {renderTaskTable(
            organizedTodos.inProgress, 
            "In Progress Tasks", 
            true, 
            showInProgress, 
            () => setShowInProgress(!showInProgress)
          )}

          {/* Completed Tasks */}
          {renderTaskTable(
            organizedTodos.completed, 
            "Completed Tasks", 
            true, 
            showCompleted, 
            () => setShowCompleted(!showCompleted)
          )}
        </div>
      )}
      
      {/* Click outside to close column settings */}
      {showColumnSettings && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setShowColumnSettings(false)}
        />
      )}
    </div>
  );
};

export default ListView; 